import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'
import { cn } from '../helpers/classnames'
import { 
  CheckCircleIcon, 
  ExclamationTriangleIcon, 
  InformationCircleIcon,
  XCircleIcon 
} from '@heroicons/react/24/outline'

export interface AlertProps extends HTMLAttributes<HTMLDivElement> {
  status?: 'info' | 'warning' | 'success' | 'error'
  variant?: 'subtle' | 'solid' | 'left-accent' | 'top-accent'
  children: ReactNode
}

export const Alert = forwardRef<HTMLDivElement, AlertProps>(
  ({ children, status = 'info', variant = 'subtle', className, ...rest }, ref) => {
    const variantClasses = {
      subtle: {
        info: 'bg-blue-50 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200',
        warning: 'bg-yellow-50 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200',
        success: 'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-200',
        error: 'bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-200'
      },
      solid: {
        info: 'bg-blue-500 text-white',
        warning: 'bg-yellow-500 text-white',
        success: 'bg-green-500 text-white',
        error: 'bg-red-500 text-white'
      },
      'left-accent': {
        info: 'border-l-4 border-blue-500 bg-blue-50 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200',
        warning: 'border-l-4 border-yellow-500 bg-yellow-50 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200',
        success: 'border-l-4 border-green-500 bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-200',
        error: 'border-l-4 border-red-500 bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-200'
      },
      'top-accent': {
        info: 'border-t-4 border-blue-500 bg-blue-50 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200',
        warning: 'border-t-4 border-yellow-500 bg-yellow-50 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200',
        success: 'border-t-4 border-green-500 bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-200',
        error: 'border-t-4 border-red-500 bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-200'
      }
    }

    return (
      <div
        ref={ref}
        role="alert"
        className={cn(
          'flex items-start p-4 rounded-md',
          variantClasses[variant][status],
          className
        )}
        {...rest}
      >
        {children}
      </div>
    )
  }
)

Alert.displayName = 'Alert'

export interface AlertIconProps extends HTMLAttributes<HTMLDivElement> {
  status?: 'info' | 'warning' | 'success' | 'error'
}

export const AlertIcon = forwardRef<HTMLDivElement, AlertIconProps>(
  ({ status = 'info', className, ...rest }, ref) => {
    const icons = {
      info: InformationCircleIcon,
      warning: ExclamationTriangleIcon,
      success: CheckCircleIcon,
      error: XCircleIcon
    }
    
    const Icon = icons[status]

    return (
      <div
        ref={ref}
        className={cn('flex-shrink-0 mr-3', className)}
        {...rest}
      >
        <Icon className="h-5 w-5" />
      </div>
    )
  }
)

AlertIcon.displayName = 'AlertIcon'

export interface AlertTitleProps extends HTMLAttributes<HTMLDivElement> {}

export const AlertTitle = forwardRef<HTMLDivElement, AlertTitleProps>(
  ({ children, className, ...rest }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('font-medium', className)}
        {...rest}
      >
        {children}
      </div>
    )
  }
)

AlertTitle.displayName = 'AlertTitle'

export interface AlertDescriptionProps extends HTMLAttributes<HTMLDivElement> {}

export const AlertDescription = forwardRef<HTMLDivElement, AlertDescriptionProps>(
  ({ children, className, ...rest }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('text-sm', className)}
        {...rest}
      >
        {children}
      </div>
    )
  }
)

AlertDescription.displayName = 'AlertDescription'